import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Form, Field, FormSpy } from 'react-final-form'
import { translate } from 'components/shared/internationalization'
import {
    SelectSectionField,
    SectionsRequestStatus
} from 'components/minutetaker/components/form/SelectSectionField'
import { AssigneeChipInputField } from 'components/minutetaker/components/form/AssigneeChipInputField'
import { DateField } from '../form/DateField'
import TextLengthCounter from 'components/shared/TextLengthCounter'
import { InputLabel, Checkbox, Typography } from '@material-ui/core'
import { NewDialogTextField } from 'components/shared/StyledComponents'
import { makeStyles } from '@material-ui/core/styles'
import { ColorBaseWhite } from 'assets/styles/variables'
import {
    getIntialValuesActions,
    getPreselectedSectionId,
    updateActionWithValues,
    validateActionValues
} from '../InvitationView/util'
import { CurrentMinutesProps, MinutesSectionsData } from 'Types'
import i18n from 'common/util/internationalization'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import containsHTML from 'components/minutetaker/components/Utils/Utils'
import { handleActionTabNavigation } from 'common/util/utmUtils'
import ReminderManager from './ReminderManager'
import { isValid, parse } from 'date-fns'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import { canCompleteAction } from 'common/util/unlinkedActions'
import type { FormApi } from 'final-form'

const EMPTY_REMINDERS: any[] = []
type Props = {
    action: any
    allContacts: Attendee[]
    dateInputMask: string
    sections: MinutesSectionsData[]
    sectionsRequestStatus?: SectionsRequestStatus
    selectedSection: any
    onActionItemSubmit: any
    setShowSaveButton: React.Dispatch<React.SetStateAction<boolean>>
    actions: any
    isAdmin: boolean
    currentMinuteItem: CurrentMinutesProps
    setIsOpen: any
    setSelectedAction: any
    history: any
    onFormValuesChange?: (values: any) => void
}

const useStyles = makeStyles((theme) => ({
    textFieldContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        minHeight: 50,
        width: '100%',
        placeItems: 'center'
    },
    formControl: {
        width: '100%',
        marginTop: theme.spacing(1.3)
    },
    inputLabel: {
        marginTop: theme.spacing(1.3),
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: '16px',
        textAlign: 'left',
        color: '#1E1E1E'
    },
    textField: {
        minHeight: 40,
        marginTop: theme.spacing(1),
        '& p': {
            marginLeft: 0,
            marginRight: 0
        }
    },
    dateField: {
        width: '100%',
        minWidth: '580px',
        paddingBottom: '10px',
        '& > div': {
            width: '100%'
        },
        '& > div > div': {
            width: '100%'
        },
        '& > *': {
            width: '100% !important'
        },
        [theme.breakpoints.down('md')]: {
            paddingTop: '12px',
            paddingBottom: '12px',
            '& > *': {
                width: '100% !important',
                display: 'contents'
            }
        }
    },
    sectionField: {
        paddingTop: '0px !important'
    },
    checkbox: {
        padding: '0px',
        '&.Mui-checked': {
            color: '#2F3B4D'
        },
        '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)'
        },
        marginRight: '8px',
        width: '24px',
        height: '24px',
        '&.Mui-focusVisible, &:active': {
            boxShadow: `0px 0px 0px 3px #0774EE, 0px 0px 0px 1px ${ColorBaseWhite}`,
            borderRadius: '4px'
        }
    },
    checkboxTitle: {
        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '18px',
        letterSpacing: '0px',
        textAlign: 'left',
        padding: '0px',
        color: '#000000'
    },
    checkBoxContainer: {
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: '16px',
        letterSpacing: '0px',
        textAlign: 'left',
        paddingBottom: '8px',
        placeItems: 'center',
        display: 'flex'
    },
    btnClass: {
        padding: '8px 12px 8px 12px'
    }
}))
const ActionEditFormRightPanel: React.FC<Props> = ({
    action = {},
    selectedSection,
    dateInputMask,
    sections,
    sectionsRequestStatus,
    allContacts,
    onActionItemSubmit,
    isAdmin,
    setShowSaveButton,
    setIsOpen,
    setSelectedAction,
    history,
    onFormValuesChange
}) => {
    const classes = useStyles()
    const [maxLength, setMaxLength] = useState(0)
    const [hasReminderErrors, setHasReminderErrors] = useState(false)
    const actionTextRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        setMaxLength(action?.action_title?.length)
        setTimeout(() => {
            if (actionTextRef.current) {
                actionTextRef.current.focus()
            }
        }, 5)
    }, [action])

    const handleSubmit = useCallback(
        async ({ action, values }: { action: any; values: any }) => {
            const initialRememberSettings = action.remember_reminder_settings

            updateActionWithValues(action, values, dateInputMask)
            action.isAdmin = isAdmin

            const shouldIncludeReminderData = isAdmin

            if (shouldIncludeReminderData) {
                action.remember_reminder_settings = values.remember_reminder_settings
                action.reminders = values.reminders || []

                if (
                    initialRememberSettings === true &&
                    values.remember_reminder_settings === false
                ) {
                    transientStorageManager.reminderUserDefaults = []
                }
            } else {
                if (!action.remember_reminder_settings && !action.reminders?.length) {
                    delete action.remember_reminder_settings
                    delete action.reminders
                }
            }

            await onActionItemSubmit(action)
            setIsOpen(false)
            setSelectedAction(null)
            handleActionTabNavigation(history)
        },
        [onActionItemSubmit, dateInputMask, setIsOpen, setSelectedAction, history, isAdmin]
    )

    const handleReminderValidationChange = useCallback((hasErrors: boolean) => {
        setHasReminderErrors(hasErrors)
    }, [])

    const initialValues = useMemo(() => {
        const baseValues = getIntialValuesActions(action, selectedSection, sections, dateInputMask)

        const apiReminders =
            action.reminders && action.reminders.length > 0 ? action.reminders : EMPTY_REMINDERS

        return {
            ...baseValues,
            reminders: apiReminders
        }
    }, [action, selectedSection, sections, dateInputMask])

    const editingActionId = action?.id
    const initialActionSection = initialValues.action_section

    /** Remount only when editing a different action — avoids resetting ReminderManager on section id sync. */
    const formInstanceKey = useMemo(() => `right-drawer-action-${editingActionId ?? 'unknown'}`, [
        editingActionId
    ])

    const formApiRef = useRef<FormApi<any> | null>(null)

    useEffect(() => {
        const form = formApiRef.current
        if (!form) return
        const target = initialActionSection
        const current = form.getState().values?.action_section
        if (String(target ?? '') !== String(current ?? '')) {
            form.change('action_section', target)
        }
    }, [initialActionSection, editingActionId])

    const isCompletionRestricted = !canCompleteAction(
        {
            self_manage: initialValues?.self_manage,
            from_deleted_committee: action?.from_deleted_committee
        },
        isAdmin
    )
    const handleValidation = useCallback(
        (values: any) => {
            const fieldErrors = validateActionValues(values)
            setShowSaveButton(
                Object.keys(fieldErrors).length === 0 &&
                    maxLength <= 10000 &&
                    !containsHTML(values.action_title) &&
                    !containsHTML(values.action_notes) &&
                    !hasReminderErrors
            )
            return fieldErrors
        },
        [maxLength, setShowSaveButton, hasReminderErrors]
    )
    const adminRestrictedChange = i18n.t('ADMIN_RESTRICTED_CHANGE')

    return (
        <Form
            key={formInstanceKey}
            initialValues={initialValues}
            onSubmit={(values) => handleSubmit({ values, action })}
            validate={(values) => handleValidation(values)}
            keepDirtyOnReinitialize={true}
            render={({ errors, handleSubmit, form, values }) => {
                formApiRef.current = form
                if (onFormValuesChange) {
                    onFormValuesChange(values)
                }

                return (
                    <>
                        <h2>{translate('EDIT_ACTION_DETAILS')}</h2>
                        <form id="editActionsForm" onSubmit={handleSubmit}>
                            <FormSpy
                                subscription={{ submitFailed: true }}
                                onChange={({ submitFailed }) => {
                                    form.getRegisteredFields().forEach((field) =>
                                        form.mutators.touched(field)
                                    )
                                    if (submitFailed) {
                                        form.getRegisteredFields().forEach((field) =>
                                            form.mutators.touched(field)
                                        )
                                    }
                                }}
                            />
                            <div className={classes.textFieldContainer}>
                                <Field
                                    name="action_completed"
                                    disabled={isCompletionRestricted}
                                    render={({ input: { onChange, checked, value, ...rest } }) => {
                                        return (
                                            <div className={classes.textFieldContainer}>
                                                <AtlasTooltip
                                                    placement="top"
                                                    title={
                                                        isCompletionRestricted
                                                            ? adminRestrictedChange
                                                            : ''
                                                    }>
                                                    <span className={classes.checkBoxContainer}>
                                                        <Checkbox
                                                            disableFocusRipple
                                                            disabled={isCompletionRestricted}
                                                            disableRipple
                                                            className={classes.checkbox}
                                                            style={{
                                                                color: isCompletionRestricted
                                                                    ? 'rgba(0, 0, 0, 0.38)'
                                                                    : '#000000'
                                                            }}
                                                            checked={checked}
                                                            aria-checked="mixed"
                                                            inputProps={
                                                                {
                                                                    'data-analytics': checked
                                                                        ? 'MM-ActionCompletionActionSummaryPageEnable'
                                                                        : 'MM-ActionCompletionActionSummaryPageDisable'
                                                                } as React.InputHTMLAttributes<
                                                                    HTMLInputElement
                                                                >
                                                            }
                                                            role="checkbox"
                                                            color="default"
                                                            onChange={onChange}
                                                            {...rest}></Checkbox>
                                                        <Typography
                                                            className={classes.checkboxTitle}
                                                            style={{
                                                                cursor: isCompletionRestricted
                                                                    ? 'default'
                                                                    : 'pointer',
                                                                color: isCompletionRestricted
                                                                    ? 'rgba(0, 0, 0, 0.38)'
                                                                    : '#000000'
                                                            }}
                                                            onClick={() =>
                                                                !isCompletionRestricted &&
                                                                onChange(!checked)
                                                            }>
                                                            {translate('COMPLETE')}
                                                        </Typography>
                                                    </span>
                                                </AtlasTooltip>
                                            </div>
                                        )
                                    }}
                                    type="checkbox"
                                />
                            </div>
                            <div className={classes.textFieldContainer}>
                                <Field
                                    name="action_title"
                                    render={({
                                        input: { onChange, ...rest },
                                        meta: { touched }
                                    }) => (
                                        <div className={classes.textFieldContainer}>
                                            <div style={{ width: '100%' }}>
                                                <InputLabel
                                                    className={classes.inputLabel}
                                                    htmlFor="action_title"
                                                    required
                                                    aria-required="true">
                                                    {translate('ACTION')}
                                                </InputLabel>
                                                <AtlasTooltip
                                                    placement="top"
                                                    title={!isAdmin ? adminRestrictedChange : ''}>
                                                    <span>
                                                        <NewDialogTextField
                                                            className={classes.textField}
                                                            multiline={true}
                                                            variant="outlined"
                                                            required
                                                            disabled={!isAdmin}
                                                            aria-required="true"
                                                            error={
                                                                !!touched &&
                                                                !!errors &&
                                                                !!errors.action_title
                                                            }
                                                            helperText={
                                                                <TextLengthCounter
                                                                    errorText={
                                                                        rest.value.length >= 10001
                                                                            ? translate(
                                                                                  'MAX_LENGTH'
                                                                              )
                                                                            : containsHTML(
                                                                                  rest.value
                                                                              )
                                                                            ? i18n.t(
                                                                                  'NO_HTML_MESSAGE'
                                                                              )
                                                                            : touched
                                                                            ? !!errors &&
                                                                              errors.action_title
                                                                            : ''
                                                                    }
                                                                    maxValue={10000}
                                                                    value={rest.value}
                                                                    colour={true}
                                                                />
                                                            }
                                                            onChange={(event) => {
                                                                setMaxLength(
                                                                    event.target.value.length
                                                                )
                                                                onChange(event)
                                                            }}
                                                            {...rest}
                                                        />
                                                    </span>
                                                </AtlasTooltip>
                                            </div>
                                        </div>
                                    )}
                                    type="text"
                                />
                            </div>
                            <SelectSectionField
                                name="action_section"
                                sections={sections}
                                sectionsRequestStatus={sectionsRequestStatus}
                                selectedSection={getPreselectedSectionId({
                                    action,
                                    selectedSection,
                                    sections
                                } as any)}
                                isReadOnly={!isAdmin}
                                showCreateNewSection={false}
                                error={!!errors && errors.action_section}
                                className={classes.sectionField}
                            />
                            <div className={classes.textFieldContainer}>
                                <DateField
                                    error={!!errors && errors.action_date}
                                    dateInputMask={dateInputMask}
                                    className={classes.dateField}
                                    disabled={!isAdmin}
                                    labelText={'REVIEWER_DUE_DATE'}
                                />
                            </div>
                            <div className={isAdmin ? classes.textFieldContainer : ''}>
                                <ReminderManager
                                    actionSummary={true}
                                    dueDate={
                                        values.action_date
                                            ? isValid(values.action_date)
                                                ? values.action_date
                                                : parse(
                                                      values.action_date,
                                                      dateInputMask,
                                                      new Date()
                                                  )
                                            : action.dueDate
                                    }
                                    initialReminders={initialValues.reminders}
                                    initialRememberSettings={action.remember_reminder_settings}
                                    onValidationChange={handleReminderValidationChange}
                                    disabled={!isAdmin}
                                    action={action}
                                    dateInputMask={dateInputMask}
                                    assignees={values.action_assignees}
                                />
                            </div>
                            <AssigneeChipInputField
                                isAdmin={isAdmin}
                                allContacts={allContacts}
                                hideAssigneeInfo={true}
                            />
                            {isAdmin && values.action_assignees?.length > 0 && (
                                <div className={classes.textFieldContainer}>
                                    <Field
                                        name="self_manage"
                                        render={({
                                            input: { onChange, checked, value, ...rest }
                                        }) => {
                                            return (
                                                <div className={classes.textFieldContainer}>
                                                    <div style={{ width: '100%' }}>
                                                        <div className={classes.checkBoxContainer}>
                                                            <Checkbox
                                                                disableFocusRipple
                                                                disableRipple
                                                                className={classes.checkbox}
                                                                checked={checked}
                                                                aria-checked="mixed"
                                                                role="checkbox"
                                                                color="default"
                                                                disabled={!isAdmin}
                                                                onClick={() => onChange(!checked)}
                                                                onChange={onChange}
                                                                inputProps={
                                                                    {
                                                                        'data-analytics': checked
                                                                            ? 'MM-SelfManageCheckboxFromActionEnable'
                                                                            : 'MM-SelfManageCheckboxFromActionDisable'
                                                                    } as React.InputHTMLAttributes<
                                                                        HTMLInputElement
                                                                    >
                                                                }
                                                                {...rest}></Checkbox>
                                                            <Typography
                                                                className={classes.checkboxTitle}
                                                                onClick={() => onChange(!checked)}>
                                                                {translate('SELF_MANAGE')}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }}
                                        type="checkbox"
                                    />
                                </div>
                            )}
                            <div className={classes.textFieldContainer}>
                                <Field
                                    name="action_notes"
                                    render={({
                                        input: { onChange, ...rest },
                                        meta: { touched }
                                    }) => (
                                        <div className={classes.textFieldContainer}>
                                            <div style={{ width: '100%' }}>
                                                <InputLabel
                                                    className={classes.inputLabel}
                                                    htmlFor="action_notes">
                                                    {translate('NOTES')}
                                                </InputLabel>
                                                <div>
                                                    <NewDialogTextField
                                                        className={classes.textField}
                                                        multiline={true}
                                                        variant="outlined"
                                                        error={
                                                            !!touched &&
                                                            !!errors &&
                                                            !!errors.action_notes
                                                        }
                                                        helperText={
                                                            <TextLengthCounter
                                                                errorText={
                                                                    rest.value.length >= 10001
                                                                        ? translate('MAX_LENGTH')
                                                                        : containsHTML(rest.value)
                                                                        ? i18n.t('NO_HTML_MESSAGE')
                                                                        : touched
                                                                        ? !!errors &&
                                                                          errors.action_notes
                                                                        : ''
                                                                }
                                                                maxValue={10000}
                                                                value={rest.value}
                                                                colour={true}
                                                            />
                                                        }
                                                        onChange={(event) => {
                                                            setMaxLength(event.target.value.length)
                                                            onChange(event)
                                                        }}
                                                        {...rest}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    type="text"
                                />
                            </div>
                        </form>
                    </>
                )
            }}
        />
    )
}

export default ActionEditFormRightPanel
